/**
 * Procedural sprite drawing on a 16x16 master grid (docs/04 A1.1 R1), 1px ink
 * outline (R2), no anti-aliasing, no gradients. Everything is drawn at unit
 * scale into a 16x16 offscreen canvas and blitted with integer scaling, which
 * is what keeps the pixel-art look crisp.
 */
import type { Rotation, Side, TileId } from '../core/sim/tiles.ts';

export const CHROME = {
  ink: '#000000',
  paper: '#f4f4f4',
  steel: '#5a6988',
  steelDark: '#39435c',
  steelLight: '#8fa0c0',
  panel: '#1c1f4a',
  panelDark: '#10101c',
  amber: '#f8f919',
  danger: '#e80709',
  pipe: '#9aa8c4',
  pipeDark: '#4a5570',
  pipeHilite: '#d3dcec',
  grid: '#232a44',
  gridLine: '#2f3859',
} as const;

export const TILE_PX = 16;

/** Rotate the canvas about the centre of a tile-sized box. */
function withRotation(
  ctx: CanvasRenderingContext2D,
  rot: Rotation,
  size: number,
  draw: () => void,
): void {
  if (rot === 0) {
    draw();
    return;
  }
  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.translate(-size / 2, -size / 2);
  draw();
  ctx.restore();
}

/** A pipe channel from the centre to one edge, at unit (16px) scale. */
function channel(ctx: CanvasRenderingContext2D, side: Side, s: number): void {
  const w = 6 * (s / TILE_PX); // channel width
  const c = s / 2;
  const half = w / 2;
  ctx.fillStyle = CHROME.pipeDark;
  switch (side) {
    case 'N':
      ctx.fillRect(c - half, 0, w, c + half);
      break;
    case 'S':
      ctx.fillRect(c - half, c - half, w, c + half);
      break;
    case 'W':
      ctx.fillRect(0, c - half, c + half, w);
      break;
    case 'E':
      ctx.fillRect(c - half, c - half, c + half, w);
      break;
  }
  // inner bore, lighter, 2px inset
  const i = 2 * (s / TILE_PX);
  ctx.fillStyle = CHROME.pipe;
  switch (side) {
    case 'N':
      ctx.fillRect(c - half + i, 0, w - 2 * i, c + half - i);
      break;
    case 'S':
      ctx.fillRect(c - half + i, c - half + i, w - 2 * i, c + half - i);
      break;
    case 'W':
      ctx.fillRect(0, c - half + i, c + half - i, w - 2 * i);
      break;
    case 'E':
      ctx.fillRect(c - half + i, c - half + i, c + half - i, w - 2 * i);
      break;
  }
}

function bevelBox(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  fill: string,
  light: string = CHROME.steelLight,
  dark: string = CHROME.steelDark,
): void {
  ctx.fillStyle = fill;
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = light;
  ctx.fillRect(x, y, w, 1);
  ctx.fillRect(x, y, 1, h);
  ctx.fillStyle = dark;
  ctx.fillRect(x, y + h - 1, w, 1);
  ctx.fillRect(x + w - 1, y, 1, h);
}

export interface TileDrawOpts {
  tile: TileId;
  rotation: Rotation;
  size: number;
  color?: string;
  locked?: boolean;
  /** splitter flap direction */
  next?: 'cw' | 'ccw';
}

/** Draw a tile filling a size x size box at the current origin. */
export function drawTile(ctx: CanvasRenderingContext2D, o: TileDrawOpts): void {
  const s = o.size;
  const u = s / TILE_PX; // one master pixel in device px
  const c = s / 2;

  switch (o.tile) {
    case 'pipe_straight':
      withRotation(ctx, o.rotation, s, () => {
        channel(ctx, 'N', s);
        channel(ctx, 'S', s);
      });
      break;

    case 'pipe_corner':
      withRotation(ctx, o.rotation, s, () => {
        channel(ctx, 'N', s);
        channel(ctx, 'E', s);
      });
      break;

    case 'pipe_cross':
      // E-W lane drawn as a bridge over N-S (docs/02 2.5).
      channel(ctx, 'N', s);
      channel(ctx, 'S', s);
      ctx.fillStyle = CHROME.ink;
      ctx.fillRect(0, c - 4 * u, s, 8 * u);
      channel(ctx, 'W', s);
      channel(ctx, 'E', s);
      break;

    case 'mixer':
      withRotation(ctx, o.rotation, s, () => {
        channel(ctx, 'W', s);
        channel(ctx, 'E', s);
        channel(ctx, 'S', s);
        // drum
        bevelBox(ctx, 3 * u, 3 * u, 10 * u, 10 * u, CHROME.steel);
        ctx.fillStyle = CHROME.panelDark;
        ctx.fillRect(5 * u, 5 * u, 6 * u, 6 * u);
        // rivets
        ctx.fillStyle = CHROME.steelLight;
        ctx.fillRect(4 * u, 4 * u, u, u);
        ctx.fillRect(11 * u, 4 * u, u, u);
        ctx.fillRect(4 * u, 11 * u, u, u);
        ctx.fillRect(11 * u, 11 * u, u, u);
      });
      break;

    case 'splitter':
      withRotation(ctx, o.rotation, s, () => {
        channel(ctx, 'N', s);
        channel(ctx, 'W', s);
        channel(ctx, 'E', s);
        bevelBox(ctx, 4 * u, 4 * u, 8 * u, 8 * u, CHROME.steel);
        // flap points at the next outlet
        ctx.fillStyle = CHROME.amber;
        if (o.next === 'ccw') ctx.fillRect(4 * u, 7 * u, 4 * u, 2 * u);
        else ctx.fillRect(8 * u, 7 * u, 4 * u, 2 * u);
      });
      break;

    case 'vat':
      withRotation(ctx, o.rotation, s, () => {
        channel(ctx, 'S', s);
        bevelBox(ctx, u, u, 14 * u, 12 * u, CHROME.steel);
        // window showing the paint
        ctx.fillStyle = o.color ?? CHROME.panelDark;
        ctx.fillRect(3 * u, 3 * u, 10 * u, 8 * u);
        ctx.fillStyle = CHROME.ink;
        ctx.strokeStyle = CHROME.ink;
        ctx.lineWidth = u;
        ctx.strokeRect(3 * u - u / 2, 3 * u - u / 2, 10 * u + u, 8 * u + u);
      });
      break;

    case 'bucket':
      withRotation(ctx, o.rotation, s, () => {
        channel(ctx, 'N', s);
        // tapered bucket body
        ctx.fillStyle = CHROME.steelDark;
        ctx.beginPath();
        ctx.moveTo(2 * u, 5 * u);
        ctx.lineTo(14 * u, 5 * u);
        ctx.lineTo(12 * u, 15 * u);
        ctx.lineTo(4 * u, 15 * u);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = CHROME.steel;
        ctx.fillRect(3 * u, 5 * u, 10 * u, 2 * u);
      });
      break;

    case 'wall':
      bevelBox(ctx, 0, 0, s, s, CHROME.steelDark, CHROME.steel, CHROME.panelDark);
      // hazard stripes
      ctx.fillStyle = CHROME.amber;
      for (let i = -16; i < 16; i += 6) {
        ctx.beginPath();
        ctx.moveTo((i + 0) * u, 0);
        ctx.lineTo((i + 3) * u, 0);
        ctx.lineTo((i + 3 + 16) * u, 16 * u);
        ctx.lineTo((i + 16) * u, 16 * u);
        ctx.closePath();
        ctx.save();
        ctx.globalAlpha = 0.55;
        ctx.fill();
        ctx.restore();
      }
      break;
  }

  if (o.locked) {
    // a locked tile wears a padlock corner (docs/02 2.10)
    ctx.fillStyle = CHROME.danger;
    ctx.fillRect(s - 5 * u, u, 4 * u, 4 * u);
    ctx.fillStyle = CHROME.ink;
    ctx.fillRect(s - 4 * u, 2 * u, 2 * u, 2 * u);
  }
}

/** A paint blob (a "can") with its colour-blind glyph. */
export function drawBlob(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  hex: string,
  glyph: string,
  units: number,
  shaded: boolean,
): void {
  const r = size * 0.3;
  ctx.fillStyle = CHROME.ink;
  ctx.beginPath();
  ctx.arc(cx, cy, r + Math.max(1, size / 16), 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = hex;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  if (shaded) {
    // 50% checker dither, the only transparency the art rules allow (R5).
    const step = Math.max(1, Math.floor(size / 16));
    ctx.fillStyle = CHROME.ink;
    for (let y = -r; y < r; y += step) {
      for (let x = -r; x < r; x += step) {
        if ((Math.round(x / step) + Math.round(y / step)) % 2 === 0) continue;
        if (x * x + y * y > r * r) continue;
        ctx.fillRect(cx + x, cy + y, step, step);
      }
    }
  }

  // glyph + unit count
  ctx.fillStyle = CHROME.ink;
  ctx.font = `bold ${Math.floor(size * 0.34)}px ui-monospace, Menlo, monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(glyph, cx, cy + size * 0.02);

  if (units > 1) {
    const bx = cx + r * 0.7;
    const by = cy - r * 0.9;
    ctx.fillStyle = CHROME.ink;
    ctx.beginPath();
    ctx.arc(bx, by, size * 0.16, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = CHROME.amber;
    ctx.font = `bold ${Math.floor(size * 0.22)}px ui-monospace, Menlo, monospace`;
    ctx.fillText(String(units), bx, by + size * 0.01);
  }
}

export { bevelBox };
